import { useCallback, useState } from 'react';

import { ChevronDown } from 'lucide-react';

import ExtractionContent from './ExtractionContent';
import NoAnswer from './NoAnswer';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { TextWithReferences } from '@/helpers/formatReferences';
import { cn } from '@/helpers/shadCnUtils';
import { KimMode, ThreadMessage } from '@/types';

interface AnswerContentProps {
  answerMessage: ThreadMessage | undefined;
  flowType: KimMode | undefined;
}

const AnswerContent = ({ answerMessage, flowType }: AnswerContentProps) => {
  const [showReasoning, setShowReasoning] = useState(false);

  const getContentFromFlowType = useCallback(
    (flowType: KimMode | undefined) => {
      if (flowType === KimMode.ExtractionFlow) {
        try {
          const maybeArray = JSON.parse(answerMessage?.content || '');

          if (Array.isArray(maybeArray)) {
            return <ExtractionContent data={maybeArray} references={answerMessage?.references ?? undefined} />;
          }
        } catch {}
      }

      if (flowType === KimMode.DraftFlow) {
        return (
          <div className="my-4 flex w-full flex-col items-center">
            <div className="relative min-h-[800px] w-full max-w-[800px] overflow-x-auto rounded-lg border bg-white px-12 py-10 shadow-lg">
              <div className="markdown-content relative text-justify text-sm text-gray-800">
                <TextWithReferences
                  content={answerMessage?.content || ''}
                  referencesDict={answerMessage?.references || {}}
                  onReferenceClick={() => {
                    trackCustomEvent(TrackingEvents.KimReferenceClicked);
                  }}
                />
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="markdown-content text-sm text-gray-800">
          <TextWithReferences
            content={answerMessage?.content || ''}
            referencesDict={answerMessage?.references || {}}
            onReferenceClick={() => {
              trackCustomEvent(TrackingEvents.KimReferenceClicked);
            }}
          />
        </div>
      );
    },
    [answerMessage],
  );

  const hasNoContent = !answerMessage || !answerMessage.content;

  return (
    <div className="rounded-lg border px-4.75 py-3">
      {hasNoContent ? (
        <NoAnswer />
      ) : (
        <>
          {answerMessage?.reasoning && answerMessage.reasoning.trim() !== '' && (
            <div className="mb-2">
              <button
                className="flex items-center gap-1 rounded-lg px-0.5 py-1.5 text-xs font-medium text-gray-700 transition-all duration-200 hover:text-gray-900"
                onClick={() => {
                  const newState = !showReasoning;
                  setShowReasoning(newState);
                  trackCustomEvent(TrackingEvents.KimToggleThinking, {
                    action: newState ? 'show' : 'hide',
                  });
                }}
              >
                <p>Show thinking</p>

                <div className="ml-auto transition-transform duration-200">
                  <ChevronDown className={cn(showReasoning && '-rotate-180', `h-4 w-4 text-gray-500 duration-200`)} />
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  showReasoning ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="my-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4">
                  <div className="markdown-content text-xs leading-relaxed text-gray-700">
                    <TextWithReferences
                      content={answerMessage?.reasoning || ''}
                      referencesDict={answerMessage?.references || {}}
                      onReferenceClick={() => {
                        trackCustomEvent(TrackingEvents.KimReferenceClicked);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {getContentFromFlowType(flowType)}
        </>
      )}
    </div>
  );
};

export default AnswerContent;
